import { FormEvent, useRef, useState } from 'react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { estimateCopy } from '@/mocks/home';

const FORM_URL = 'https://readdy.ai/api/form/dbc7noooojagos9kbgv0';
const field = 'w-full box-border rounded-[2px] border border-background-300/80 bg-background-50 px-4 py-3.5 text-[15px] text-foreground-950 placeholder:text-foreground-400 hover:border-background-400 focus:outline-none focus:border-primary-600 focus:bg-background-50 transition-colors duration-300 ease-out';
const lbl = 'block mb-2 text-[11px] font-label font-medium uppercase tracking-[0.18em] text-foreground-600 transition-colors duration-300 group-focus-within:text-primary-800';

export default function EstimateSection() {
  const ref = useRef<HTMLElement>(null);
  useSectionReveal(ref);
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [chars, setChars] = useState(0);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = String(fd.get('website_alt') || '').trim();
    if (hp) { setStatus('ok'); return; }
    fd.delete('website_alt');
    const msg = String(fd.get('message') || '');
    if (msg.length > 500) { setFormError('Message must be 500 characters or fewer.'); setStatus('error'); return; }
    if (!fd.get('name') || !fd.get('email')) { setFormError('Please add your name and email.'); setStatus('error'); return; }
    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, String(v)));
      const response = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('ok');
        form.reset();
        setChars(0);
      } else {
        setFormError(serverMsg || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setFormError('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <section ref={ref} id="estimate" className="bg-background-100 px-4 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24 border-b border-primary-950">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div data-reveal-img className="hidden lg:block lg:col-span-5 w-full h-[680px] rounded-none overflow-hidden">
          <img loading="lazy" decoding="async" src={estimateCopy.image} alt="Sunlit window sill" title="New windows bringing warm light" className="w-full h-full object-cover object-top" />
        </div>
        <div className="min-w-0 lg:col-span-7 lg:pt-6">
          <p data-reveal className="text-[11px] font-label font-medium uppercase tracking-[0.22em] text-primary-600">{estimateCopy.eyebrow}</p>
          <h2 data-reveal className="mt-4 font-heading font-light text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.015em] text-foreground-950 max-w-[14ch]">{estimateCopy.title}</h2>
          <p data-reveal className="mt-5 text-[15px] leading-relaxed text-foreground-600 max-w-md">{estimateCopy.text}</p>

          <form id="estimate-form" data-readdy-form onSubmit={onSubmit} className="mt-10 pt-10 border-t border-background-300/70 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6 relative">
            <input type="text" name="website_alt" className="ef-aux-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
            <div data-reveal className="group min-w-0"><label htmlFor="ef-name" className={lbl}>Full name *</label><input id="ef-name" name="name" required placeholder="Jane Porter" className={field} /></div>
            <div data-reveal className="group min-w-0"><label htmlFor="ef-email" className={lbl}>Email *</label><input id="ef-email" name="email" type="email" required placeholder="you@example.com" className={field} /></div>
            <div data-reveal className="group min-w-0"><label htmlFor="ef-phone" className={lbl}>Phone</label><input id="ef-phone" name="phone" type="tel" placeholder="Optional" className={field} /></div>
            <div data-reveal className="group min-w-0"><label htmlFor="ef-zip" className={lbl}>ZIP code</label><input id="ef-zip" name="zip" placeholder="97210" className={field} /></div>
            <div data-reveal className="group min-w-0 sm:col-span-2">
              <label htmlFor="ef-type" className={lbl}>Window type</label>
              <select id="ef-type" name="window_type" defaultValue="Not sure yet" className={`${field} cursor-pointer`}>
                <option>Not sure yet</option>
                <option>Casement</option>
                <option>Picture Window</option>
                <option>Roof Window</option>
                <option>Lift &amp; Slide</option>
              </select>
            </div>
            <div data-reveal className="group min-w-0 sm:col-span-2">
              <label htmlFor="ef-msg" className={lbl}>Project notes</label>
              <textarea id="ef-msg" name="message" rows={4} maxLength={500} onChange={(ev) => setChars(ev.target.value.length)} placeholder="How many windows, and anything we should know?" className={`${field} resize-none`} />
              <p className="mt-1 text-right text-xs text-foreground-500">{chars}/500</p>
            </div>
            <div data-reveal className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
              <button type="submit" disabled={status === 'sending'} aria-busy={status === 'sending'} className="group/btn w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-[2px] bg-primary-500 hover:bg-primary-700 text-background-50 px-8 py-3.5 text-sm font-medium tracking-[0.02em] whitespace-nowrap cursor-pointer transition-colors duration-[250ms] ease-out disabled:opacity-60 disabled:cursor-wait">
                {status === 'sending' ? 'Sending…' : 'Request my estimate'}
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-[250ms] ease-out group-hover/btn:translate-x-1">
                  <i className={status === 'sending' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} />
                </span>
              </button>
              <div aria-live="polite" className="min-w-0">
                {status === 'ok' && <p className="text-sm text-primary-700">Thank you — we’ll be in touch within one business day.</p>}
                {status === 'error' && <p role="alert" className="text-sm text-accent-800">{formError}</p>}
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}